import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import {
    Archive as ArchiveIcon,
    Delete as DeleteIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    Unarchive as UnarchiveIcon
} from "@mui/icons-material";
import styled from "styled-components";
import firebase from "firebase";
import { firestore, functions } from "../../services/firebase";
import supabase from "../../services/supabaseClient";
import { generateId } from "../../actions/bars";
import { THEME_MAP } from "../../theme";
import { useIsMobile } from "../../hooks/useIsMobile";
import { captureError } from "../../lib/errorReporting";

const STATUS_FILTERS = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "archived", label: "Archived" },
    { value: "all", label: "All" }
];

export default function ClaimedBarReview() {
    const isMobile = useIsMobile();
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [processingIds, setProcessingIds] = useState({});
    const [statusFilter, setStatusFilter] = useState("pending");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

    useEffect(() => {
        const unsubscribe = firestore
            .collection("claimed_bar_review")
            .orderBy("createdAt", "desc")
            .onSnapshot(
                (snapshot) => {
                    const rows = snapshot.docs.map((doc) => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.() || null,
                            processedAt: data.processedAt?.toDate?.() || null
                        };
                    });
                    setClaims(rows);
                    setLoading(false);
                },
                (snapError) => {
                    setError(
                        snapError?.message || "Unable to load claimed bars right now."
                    );
                    setLoading(false);
                }
            );

        return () => unsubscribe();
    }, []);

    const filteredClaims = useMemo(() => {
        let result = claims;

        // Status filter
        if (statusFilter !== "all") {
            result = result.filter((c) => (c.status || "pending") === statusFilter);
        }

        // Search filter
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            result = result.filter(
                (c) =>
                    (c.barName || "").toLowerCase().includes(q) ||
                    (c.barAddress || "").toLowerCase().includes(q) ||
                    (c.contactName || "").toLowerCase().includes(q) ||
                    (c.contactEmail || "").toLowerCase().includes(q)
            );
        }

        return result;
    }, [claims, statusFilter, search]);

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [statusFilter, search]);

    const paginatedClaims = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredClaims.slice(start, start + rowsPerPage);
    }, [filteredClaims, page, rowsPerPage]);

    const totalPages = Math.ceil(filteredClaims.length / rowsPerPage);

    const markProcessing = (id, value) => {
        setProcessingIds((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const updateUserAssignment = async (claim) => {
        const usersCollection = firestore.collection("Users");
        const updates = {
            unassigned_to_bar: false,
            last_claim_approved_at: firebase.firestore.FieldValue.serverTimestamp()
        };

        // Mark every user this approval assigns: the submitter (userId/userEmail)
        // and the actual manager named on the claim (contactEmail), which is a
        // different person when an admin submits the claim on someone's behalf.
        const refs = new Map();

        if (claim.userId) {
            refs.set(claim.userId, usersCollection.doc(claim.userId));
        }

        const emails = [claim.userEmail, claim.contactEmail]
            .map((email) => (email || "").trim().toLowerCase())
            .filter(Boolean);

        for (const email of emails) {
            // eslint-disable-next-line no-await-in-loop
            const snapshot = await usersCollection
                .where("email", "==", email)
                .limit(1)
                .get();
            if (!snapshot.empty) {
                const ref = snapshot.docs[0].ref;
                if (!refs.has(ref.id)) {
                    refs.set(ref.id, ref);
                }
            }
        }

        const tasks = Array.from(refs.values()).map((ref) =>
            ref.set(updates, { merge: true })
        );

        if (tasks.length) {
            await Promise.all(tasks);
        }
    };

    const handleApprove = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            let barId = claim.barId;

            // Manual ("Google didn't find my bar") claims are submitted with no
            // barId and never create a bar record. Now that an admin has
            // approved it, create the Supabase bar so the owner's profile is
            // populated, then link the manager to it.
            if (!barId || claim.lookupSource === "google_not_found") {
                const generatedId = generateId();
                const normalizedName = (claim.barName || "").trim();
                const { error: supabaseError } = await supabase
                    .from("bars")
                    .upsert(
                        {
                            bar_id: generatedId,
                            bar_name: normalizedName,
                            bar_name_lower: normalizedName
                                ? normalizedName.toLowerCase()
                                : null,
                            bar_address: claim.barAddress || "",
                            address_1: claim.barStreet || null,
                            city: claim.barCity || null,
                            state: claim.barState || null,
                            zip: claim.barZip || null,
                            created_by: claim.userId || null,
                            owner_email: claim.contactEmail || null,
                            data_source: "claim_manual"
                        },
                        { onConflict: "bar_id" }
                    );
                if (supabaseError) {
                    throw new Error(
                        supabaseError.message ||
                            "Unable to create the bar record for this claim."
                    );
                }
                barId = generatedId;
            }

            // Mark the claim approved BEFORE granting the manager role. Edit
            // access is driven by the manager role, so granting it while the
            // claim still read "pending" would let the user edit the bar before
            // approval. If the role grant fails we revert to "pending" below so
            // the admin can retry without ever leaving that window open.
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "approved",
                        processedAt: firebase.firestore.FieldValue.serverTimestamp()
                    },
                    { merge: true }
                );

            try {
                const addManager = functions.httpsCallable("addManager");
                await addManager({
                    barId,
                    barName: claim.barName,
                    role: claim.role,
                    email: claim.contactEmail
                });

                await updateUserAssignment(claim);
            } catch (assignError) {
                // Roll the claim back so the approval stays retryable and we
                // never leave it approved-but-unassigned.
                await firestore
                    .collection("claimed_bar_review")
                    .doc(claim.id)
                    .set({ status: "pending", processedAt: null }, { merge: true });
                throw assignError;
            }
        } catch (approveError) {
            captureError(approveError, { view: "ClaimedBarReview", action: "approveClaim" });
            setError(
                approveError?.message || "Unable to approve this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const handleReject = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "rejected",
                        processedAt: firebase.firestore.FieldValue.serverTimestamp()
                    },
                    { merge: true }
                );
        } catch (rejectError) {
            captureError(rejectError, { view: "ClaimedBarReview", action: "rejectClaim" });
            setError(
                rejectError?.message || "Unable to reject this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const handleArchive = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "archived",
                        archivedAt: firebase.firestore.FieldValue.serverTimestamp()
                    },
                    { merge: true }
                );
        } catch (archiveError) {
            captureError(archiveError, { view: "ClaimedBarReview", action: "archiveClaim" });
            setError(
                archiveError?.message || "Unable to archive this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const handleUnarchive = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        try {
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .set(
                    {
                        status: "pending",
                        archivedAt: firebase.firestore.FieldValue.delete()
                    },
                    { merge: true }
                );
        } catch (unarchiveError) {
            captureError(unarchiveError, { view: "ClaimedBarReview", action: "unarchiveClaim" });
            setError(
                unarchiveError?.message || "Unable to unarchive this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const handleDelete = async (claim) => {
        markProcessing(claim.id, true);
        setError("");
        setConfirmDialog(null);
        try {
            await firestore
                .collection("claimed_bar_review")
                .doc(claim.id)
                .delete();
        } catch (deleteError) {
            captureError(deleteError, { view: "ClaimedBarReview", action: "deleteClaim" });
            setError(
                deleteError?.message || "Unable to delete this claim right now."
            );
        } finally {
            markProcessing(claim.id, false);
        }
    };

    const statusChipColor = (status) => {
        switch (status) {
            case "approved":
                return "success";
            case "rejected":
                return "error";
            case "archived":
                return "default";
            default:
                return "warning";
        }
    };

    const activeFilterCount =
        (statusFilter !== "pending" ? 1 : 0);

    const renderActionButtons = (claim) => {
        const isProcessing = Boolean(processingIds[claim.id]);
        const status = claim.status || "pending";

        return (
            <Box display="flex" gap={0.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
                {status === "pending" && (
                    <>
                        <Button
                            size="small"
                            color="success"
                            variant="contained"
                            disabled={isProcessing}
                            onClick={() => handleApprove(claim)}
                        >
                            {isProcessing ? "..." : "Approve"}
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            disabled={isProcessing}
                            onClick={() => handleReject(claim)}
                        >
                            Reject
                        </Button>
                    </>
                )}
                {status === "archived" ? (
                    <Tooltip title="Unarchive">
                        <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => handleUnarchive(claim)}
                        >
                            <UnarchiveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <Tooltip title="Archive">
                        <IconButton
                            size="small"
                            disabled={isProcessing}
                            onClick={() => handleArchive(claim)}
                        >
                            <ArchiveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Tooltip title="Delete permanently">
                    <IconButton
                        size="small"
                        color="error"
                        disabled={isProcessing}
                        onClick={() => setConfirmDialog(claim)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    };

    const renderMobileCard = (claim) => {
        const createdAt = claim.createdAt
            ? claim.createdAt.toLocaleDateString()
            : "--";
        const status = claim.status || "pending";

        return (
            <MobileCard key={claim.id}>
                <div className="card-header">
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <div>
                            <strong>{claim.barName}</strong>
                            {claim.barAddress && (
                                <div className="muted">{claim.barAddress}</div>
                            )}
                        </div>
                        <Chip
                            label={status}
                            color={statusChipColor(status)}
                            size="small"
                            sx={{ textTransform: "capitalize" }}
                        />
                    </Box>
                </div>

                <div className="card-row">
                    <span className="card-label">Contact:&nbsp;</span>
                    <span>{claim.contactName}</span>
                </div>
                <div className="card-row">
                    <span className="card-label">Email:&nbsp;</span>
                    <span className="muted">{claim.contactEmail}</span>
                </div>
                {claim.contactPhone && (
                    <div className="card-row">
                        <span className="card-label">Phone:&nbsp;</span>
                        <span className="muted">{claim.contactPhone}</span>
                    </div>
                )}
                <div className="card-row">
                    <span className="card-label">Role:&nbsp;</span>
                    <span style={{ textTransform: "capitalize" }}>{claim.role}</span>
                </div>
                <div className="card-row">
                    <span className="card-label">Lookup:&nbsp;</span>
                    <span>
                        {claim.lookupSource === "google"
                            ? "Google Places"
                            : claim.lookupSource === "google_not_found"
                            ? "Manually entered"
                            : "Existing"}
                    </span>
                </div>
                <div className="card-row">
                    <span className="card-label">Submitted:&nbsp;</span>
                    <span>{createdAt}</span>
                </div>

                {renderActionButtons(claim)}
            </MobileCard>
        );
    };

    return (
        <Container>
            {error && (
                <Alert severity="error" onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            {isMobile ? (
                <>
                    <MobileSearchRow>
                        <TextField
                            className="search-field"
                            size="small"
                            label="Search Claims"
                            variant="outlined"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                            }}
                        />
                        <IconButton
                            className="filter-icon-btn"
                            onClick={() => setMobileFilterOpen(true)}
                        >
                            <FilterListIcon />
                            {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
                        </IconButton>
                    </MobileSearchRow>

                    <Dialog
                        open={mobileFilterOpen}
                        onClose={() => setMobileFilterOpen(false)}
                        fullWidth
                        maxWidth="xs"
                    >
                        <DialogTitle>Filters</DialogTitle>
                        <DialogContent>
                            <ModalFilterStack>
                                <TextField
                                    select
                                    label="Status"
                                    size="small"
                                    fullWidth
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    {STATUS_FILTERS.map((sf) => (
                                        <MenuItem key={sf.value} value={sf.value}>
                                            {sf.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </ModalFilterStack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setStatusFilter("pending"); }}>
                                Reset
                            </Button>
                            <Button variant="contained" onClick={() => setMobileFilterOpen(false)}>
                                Apply
                            </Button>
                        </DialogActions>
                    </Dialog>
                </>
            ) : (
                <FilterRow>
                    <TextField
                        className="search-field"
                        size="small"
                        label="Search Claims"
                        variant="outlined"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />
                        }}
                    />
                    <TextField
                        className="status-select"
                        select
                        label="Status"
                        size="small"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUS_FILTERS.map((sf) => (
                            <MenuItem key={sf.value} value={sf.value}>
                                {sf.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Typography variant="body2" color="textSecondary" sx={{ ml: "auto" }}>
                        {filteredClaims.length} claim{filteredClaims.length !== 1 ? "s" : ""}
                    </Typography>
                </FilterRow>
            )}

            {/* Active filter chips */}
            {statusFilter !== "pending" && (
                <ChipsRow>
                    <Chip
                        className="filter-chip"
                        label={`Status: ${STATUS_FILTERS.find((s) => s.value === statusFilter)?.label}`}
                        size="small"
                        onDelete={() => setStatusFilter("pending")}
                    />
                </ChipsRow>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : filteredClaims.length === 0 ? (
                <Typography variant="body1" color="textSecondary">
                    {claims.length === 0
                        ? "No bar claims submitted yet."
                        : "No claims match the current filters."}
                </Typography>
            ) : isMobile ? (
                <>
                    {paginatedClaims.map(renderMobileCard)}

                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={1}>
                            <Pagination
                                count={totalPages}
                                page={page + 1}
                                onChange={(_, v) => setPage(v - 1)}
                                size="small"
                                siblingCount={0}
                            />
                        </Box>
                    )}
                </>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Submitted</TableCell>
                                <TableCell>Bar</TableCell>
                                <TableCell>Contact</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Lookup</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedClaims.map((claim) => {
                                const createdAt = claim.createdAt
                                    ? claim.createdAt.toLocaleString()
                                    : "--";
                                const status = claim.status || "pending";
                                return (
                                    <TableRow key={claim.id} hover>
                                        <TableCell>{createdAt}</TableCell>
                                        <TableCell>
                                            <strong>{claim.barName}</strong>
                                            {claim.barAddress && (
                                                <div className="muted">{claim.barAddress}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div>{claim.contactName}</div>
                                            <div className="muted">{claim.contactEmail}</div>
                                            {claim.contactPhone && (
                                                <div className="muted">{claim.contactPhone}</div>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textTransform: "capitalize" }}>
                                            {claim.role}
                                        </TableCell>
                                        <TableCell>
                                            {claim.lookupSource === "google"
                                                ? "Google Places"
                                                : claim.lookupSource === "google_not_found"
                                                ? "Manually entered"
                                                : "Existing"}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={status}
                                                color={statusChipColor(status)}
                                                size="small"
                                                sx={{ textTransform: "capitalize" }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            {renderActionButtons(claim)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={filteredClaims.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(Number(e.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage="Rows per page"
                    />
                </TableContainer>
            )}

            {/* Delete confirmation dialog */}
            <Dialog
                open={Boolean(confirmDialog)}
                onClose={() => setConfirmDialog(null)}
            >
                <DialogTitle>Delete Claim</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Permanently delete the claim for{" "}
                        <strong>{confirmDialog?.barName}</strong> by{" "}
                        {confirmDialog?.contactName || confirmDialog?.contactEmail}?
                        This cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => handleDelete(confirmDialog)}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

/* ── Styled Components ─────────────────────────────────── */

const Container = styled.div`
    height: 100%;
    width: 100%;
    overflow: auto;
    padding: 24px;
    background: ${THEME_MAP["color-basic-100"]};
    display: flex;
    flex-direction: column;
    gap: 24px;

    .muted {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
    }

    @media (max-width: 767px) {
        padding: 16px;
        gap: 12px;
    }
`;

const FilterRow = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: nowrap;
    background: ${THEME_MAP["color-basic-100"]};
    border-radius: 8px;
    padding: 12px 8px 12px 0;
    overflow-x: auto;

    .search-field {
        min-width: 200px;
        max-width: 280px;
    }

    .status-select {
        min-width: 140px;
        max-width: 180px;
    }
`;

const ChipsRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    .filter-chip {
        background: ${THEME_MAP["color-primary-100"]};
        color: ${THEME_MAP["color-primary-700"]};
        font-weight: 500;
    }
`;

const MobileSearchRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    .search-field {
        flex: 1;
        min-width: 0;
    }

    .filter-icon-btn {
        position: relative;
        color: ${THEME_MAP["color-basic-700"]};
        flex-shrink: 0;
    }
`;

const FilterBadge = styled.span`
    position: absolute;
    top: 2px;
    right: 2px;
    background: ${THEME_MAP["color-primary-500"]};
    color: #fff;
    font-size: 0.65rem;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
`;

const ModalFilterStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 8px;
`;

const MobileCard = styled.div`
    background: ${THEME_MAP["color-basic-100"]};
    border: 1px solid ${THEME_MAP["color-basic-400"]};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .card-header {
        strong {
            font-size: 0.95rem;
        }
    }

    .card-row {
        display: flex;
        align-items: center;
    }

    .card-label {
        font-size: 0.875rem;
        color: ${THEME_MAP["color-basic-700"]};
    }

    .muted {
        font-size: 0.8rem;
        color: rgba(0, 0, 0, 0.5);
    }
`;
